import { errDiv, sumValues } from "../../../util/mathHelpers.ts";
import { ORIENTATIONS } from "../../../constants/intxConstants.js";
import { Intersection } from "../Intersection.js";

// ORIENTATIONS.NORTH_SOUTH = North and south are major (free flow) approaches, East and West are stop controlled
const defaultLaneConfig = {
  T: 1,
  LT: 1,
  RT: 1,
  RTShared: false,
  LTShared: false,
};

export const TWSC_MEDIAN_OPTIONS = {
  NO: "None",
  YES_NARROW: "10-20 ft Wide",
  YES_WIDE: "20-40 ft Wide",
};

/** New Intersection Template computational class. Extends the Intersection parent class */
export class TWSC extends Intersection {
  /**
   * Constructor for the NewIntxTemplate class.
   * @param {string} name - Name of the intersection
   * @param {Object} volumes - Object mapping direction strings (eastbound, westbound, northbound, southbound) to  {@link Volume} objects.
   * @param {Object} globalParams - Object containing the required global parameters for an intersection.
   * @param {string} majorStDirection - Optional string identifier for the major street direction, default = ORIENTATIONS.NORTH_SOUTH
   */
  constructor(
    name,
    volumes,
    globalParams,
    majorStDirection = ORIENTATIONS.NORTH_SOUTH
  ) {
    super(name, volumes, globalParams);

    // Free flow direction. The other approaches are stop-controlled
    this.majorStDirection = majorStDirection;

    // init with 1 lane on every mvmt
    this.LaneConfig = TWSC.getZoneDefaultInputs();

    // Decimal (0 - 1.0) value of truck percent
    this.truckPcts =
      this.majorStDirection === ORIENTATIONS.EAST_WEST
        ? {
            Major1: Number(this.eastbound.truckDec),
            Major2: Number(this.westbound.truckDec),
            Minor1: Number(this.northbound.truckDec),
            Minor2: Number(this.southbound.truckDec),
          }
        : {
            Major1: Number(this.southbound.truckDec),
            Major2: Number(this.northbound.truckDec),
            Minor1: Number(this.eastbound.truckDec),
            Minor2: Number(this.westbound.truckDec),
          };
  }

  /**
   * Function to get the DEFAULT inputs available the intersection.  This function is designed to facilitate the
   * integration of the engine into a user interface.
   *
   * Default: North South major, East West stop controlled
   *
   * @return {Object} Object representation of default inputs
   */
  static getZoneDefaultInputs() {
    return {
      Z5: {
        EB: { ...defaultLaneConfig },
        WB: { ...defaultLaneConfig },
        NB: { ...defaultLaneConfig },
        SB: { ...defaultLaneConfig },
        median: TWSC_MEDIAN_OPTIONS.NO,
      },
    };
  }

  static getLaneConfigLabelsMapping() {
    return {
      Z5: this.getLabelsMappingWithoutMajorDir(),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    if (this.majorStDirection === ORIENTATIONS.EAST_WEST) {
      this.LaneConfig = {
        Z5: {
          Major1: {
            LT: laneConfigInputs.Z5.EB.LT,
            T: laneConfigInputs.Z5.EB.T,
            RT: laneConfigInputs.Z5.EB.RTShared ? 0 : laneConfigInputs.Z5.EB.RT,
          },
          Major2: {
            LT: laneConfigInputs.Z5.WB.LT,
            T: laneConfigInputs.Z5.WB.T,
            RT: laneConfigInputs.Z5.WB.RTShared ? 0 : laneConfigInputs.Z5.WB.RT,
          },
          Minor1: {
            LT: laneConfigInputs.Z5.NB.LTShared ? 0 : laneConfigInputs.Z5.NB.LT,
            T: laneConfigInputs.Z5.NB.T,
            RT: laneConfigInputs.Z5.NB.RTShared ? 0 : laneConfigInputs.Z5.NB.RT,
          },
          Minor2: {
            LT: laneConfigInputs.Z5.SB.LTShared ? 0 : laneConfigInputs.Z5.SB.LT,
            T: laneConfigInputs.Z5.SB.T,
            RT: laneConfigInputs.Z5.SB.RTShared ? 0 : laneConfigInputs.Z5.SB.RT,
          },
          median: laneConfigInputs.Z5.median,
        },
      };
      this.majorStDirection = ORIENTATIONS.EAST_WEST;
    } else {
      // EW stop controlled
      this.LaneConfig = {
        Z5: {
          Major1: {
            LT: laneConfigInputs.Z5.SB.LT,
            T: laneConfigInputs.Z5.SB.T,
            RT: laneConfigInputs.Z5.SB.RTShared ? 0 : laneConfigInputs.Z5.SB.RT,
          },
          Major2: {
            LT: laneConfigInputs.Z5.NB.LT,
            T: laneConfigInputs.Z5.NB.T,
            RT: laneConfigInputs.Z5.NB.RTShared ? 0 : laneConfigInputs.Z5.NB.RT,
          },
          Minor1: {
            LT: laneConfigInputs.Z5.EB.LTShared ? 0 : laneConfigInputs.Z5.EB.LT,
            T: laneConfigInputs.Z5.EB.T,
            RT: laneConfigInputs.Z5.EB.RTShared ? 0 : laneConfigInputs.Z5.EB.RT,
          },
          Minor2: {
            LT: laneConfigInputs.Z5.WB.LTShared ? 0 : laneConfigInputs.Z5.WB.LT,
            T: laneConfigInputs.Z5.WB.T,
            RT: laneConfigInputs.Z5.WB.RTShared ? 0 : laneConfigInputs.Z5.WB.RT,
          },
          median: laneConfigInputs.Z5.median,
        },
      };
      this.majorStDirection = ORIENTATIONS.NORTH_SOUTH;
    }
  }

  // Implements the computeVCAnalysis function of the Intersection parent class.
  _runCriticalMovementAnalysis() {
    this.volumes = this.getAdjustedVolume();
    // Adjust major right turn volume when there is a exclusive right turn lane
    // In some HCM euqations, if a right-turn lane exists on the major street, the corresponding v3 or v6 term in these equations may be assumed to be zero.
    const major1RtVolumeExclusiveLane =
      this.LaneConfig.Z5.Major1.RT > 0 ? 0 : this.volumes.Major1.RT;
    const major2RtVolumeExclusiveLane =
      this.LaneConfig.Z5.Major2.RT > 0 ? 0 : this.volumes.Major2.RT;

    // Init V/C result
    this._resultsByZone = {
      Z5: {
        CLV: -1,
        VC: -1,
      },
    };

    // Check for div by 0 error. If so, return -1 directly
    if (
      this.volumes.Major1.LT === 0 ||
      this.volumes.Major2.LT === 0 ||
      this.volumes.Major1.T + this.volumes.Major1.LT === 0 ||
      this.volumes.Major2.T + this.volumes.Major2.LT === 0
    ) {
      return;
    }

    // Major approach left turn and thru v/c
    this.major1ThruVc = errDiv(
      this.volumes.Major1.T,
      this.LaneConfig.Z5.Major1.T * 1800
    );
    this.major2ThruVc = errDiv(
      this.volumes.Major2.T,
      this.LaneConfig.Z5.Major2.T * 1800
    );
    this.major1LtMovementCapacity = getMajorStLtMovementCapacity({
      conflictingLanes: this.LaneConfig.Z5.Major2.T,
      conflictingVolume: this.volumes.Major2.T + this.volumes.Major2.RT,
      truckPct: this.truckPcts.Major1,
    });
    this.major1LtVc = errDiv(
      this.volumes.Major1.LT,
      this.major1LtMovementCapacity
    );
    this.major2LtMovementCapacity = getMajorStLtMovementCapacity({
      conflictingLanes: this.LaneConfig.Z5.Major1.T,
      conflictingVolume: this.volumes.Major1.T + this.volumes.Major1.RT,
      truckPct: this.truckPcts.Major2,
    });
    this.major2LtVc = errDiv(
      this.volumes.Major2.LT,
      this.major2LtMovementCapacity
    );

    // Minor approach right turn v/c
    this.minor1RtMovementCapacity = getMinorStRtMovementCapacity({
      conflictingLanes: this.LaneConfig.Z5.Major1.T,
      conflictingVolume:
        0.5 * major1RtVolumeExclusiveLane +
        (this.LaneConfig.Z5.Major1.T === 1 ? 1 : 0.5) * this.volumes.Major1.T,
      truckPct: this.truckPcts.Minor1,
    });
    this.minor1RtVc = errDiv(
      this.volumes.Minor1.RT,
      this.minor1RtMovementCapacity
    );
    this.minor2RtMovementCapacity = getMinorStRtMovementCapacity({
      conflictingLanes: this.LaneConfig.Z5.Major2.T,
      conflictingVolume:
        0.5 * major2RtVolumeExclusiveLane +
        (this.LaneConfig.Z5.Major2.T === 1 ? 1 : 0.5) * this.volumes.Major2.T,
      truckPct: this.truckPcts.Minor2,
    });
    this.minor2RtVc = errDiv(
      this.volumes.Minor2.RT,
      this.minor2RtMovementCapacity
    );
    this.major1SharedLaneCapacity = errDiv(
      this.volumes.Major1.LT,
      errDiv(this.volumes.Major1.LT, this.major1LtMovementCapacity)
    );
    const major1SharedLaneVcRatio =
      1 - errDiv(this.volumes.Major1.LT, this.major1SharedLaneCapacity);

    this.major2SharedLaneCapacity = errDiv(
      this.volumes.Major2.LT,
      errDiv(this.volumes.Major2.LT, this.major2LtMovementCapacity)
    );
    const major2SharedLaneVcRatio =
      1 - errDiv(this.volumes.Major2.LT, this.major2SharedLaneCapacity);

    // Minor approach thru v/c
    const minor1ThruHelpers = getMinorStThruHelpers({
      conflictingLanes: this.LaneConfig.Z5.Major1.T,
      conflictingVolume:
        2 * this.volumes.Major1.LT +
        this.volumes.Major1.T +
        0.5 * major1RtVolumeExclusiveLane +
        2 * this.volumes.Major2.LT +
        this.volumes.Major2.T +
        this.volumes.Major2.RT,
      opposingVolume: sumValues(this.volumes.Minor2),
      truckPct: this.truckPcts.Minor1,
    });
    this.minor1ThruMovementCapacity =
      minor1ThruHelpers.potentialCapacity *
      major1SharedLaneVcRatio *
      major2SharedLaneVcRatio;
    const minor2ThruHelpers = getMinorStThruHelpers({
      conflictingLanes: this.LaneConfig.Z5.Major2.T,
      conflictingVolume:
        2 * this.volumes.Major2.LT +
        this.volumes.Major2.T +
        0.5 * major2RtVolumeExclusiveLane +
        2 * this.volumes.Major1.LT +
        this.volumes.Major1.T +
        this.volumes.Major1.RT,
      opposingVolume: sumValues(this.volumes.Minor1),
      truckPct: this.truckPcts.Minor2,
    });
    this.minor1ThruVc = errDiv(
      this.volumes.Minor1.T,
      this.minor1ThruMovementCapacity
    );
    this.minor2ThruMovementCapacity =
      minor2ThruHelpers.potentialCapacity *
      major1SharedLaneVcRatio *
      major2SharedLaneVcRatio;
    this.minor2ThruVc = errDiv(
      this.volumes.Minor2.T,
      this.minor2ThruMovementCapacity
    );

    // Minor approach left turn v/c
    const minor1LtPPrime = getMinorStLtPPrime({
      stage1LtVolume: this.volumes.Major1.LT,
      stage1SharedLaneCapacity: this.major1SharedLaneCapacity,
      stage2LtVolume: this.volumes.Major2.LT,
      stage2SharedLaneCapacity: this.major2SharedLaneCapacity,
      opposingThruVolume: this.volumes.Minor2.T,
      opposingMovementCapacity: this.minor2ThruMovementCapacity,
    });
    const minor2LtPPrime = getMinorStLtPPrime({
      stage1LtVolume: this.volumes.Major2.LT,
      stage1SharedLaneCapacity: this.major2SharedLaneCapacity,
      stage2LtVolume: this.volumes.Major1.LT,
      stage2SharedLaneCapacity: this.major1SharedLaneCapacity,
      opposingThruVolume: this.volumes.Minor1.T,
      opposingMovementCapacity: this.minor1ThruMovementCapacity,
    });
    const minor1LtConflictingVolume =
      2 * this.volumes.Major1.LT +
      this.volumes.Major1.T +
      0.5 * major1RtVolumeExclusiveLane +
      2 * this.volumes.Major2.LT +
      (this.LaneConfig.Z5.Major1.T === 1
        ? 1
        : this.LaneConfig.Z5.Major1.T === 2
        ? 0.5
        : 0.4) *
        this.volumes.Major2.T +
      (this.LaneConfig.Z5.Major1.T === 1
        ? 0.5 * this.volumes.Major2.RT + 0.5 * this.volumes.Minor2.RT
        : 0) +
      0.5 * this.volumes.Minor2.T;
    const minor1LtHelpers = getMinorStLtHelpers({
      conflictingLanes: this.LaneConfig.Z5.Major1.T,
      conflictingVolume: minor1LtConflictingVolume,
      opposingVolume: sumValues(this.volumes.Minor2),
      truckPct: this.truckPcts.Minor1,
    });
    const minor2LtConflictingVolume =
      2 * this.volumes.Major2.LT +
      this.volumes.Major2.T +
      0.5 * major2RtVolumeExclusiveLane +
      2 * this.volumes.Major1.LT +
      (this.LaneConfig.Z5.Major2.T === 1
        ? 1
        : this.LaneConfig.Z5.Major2.T === 2
        ? 0.5
        : 0.4) *
        this.volumes.Major1.T +
      (this.LaneConfig.Z5.Major2.T === 1
        ? 0.5 * this.volumes.Major1.RT + 0.5 * this.volumes.Minor1.RT
        : 0) +
      0.5 * this.volumes.Minor1.T;
    const minor2LtHelpers = getMinorStLtHelpers({
      conflictingLanes: this.LaneConfig.Z5.Major2.T,
      conflictingVolume: minor2LtConflictingVolume,
      opposingVolume: sumValues(this.volumes.Minor1),
      truckPct: this.truckPcts.Minor2,
    });
    this.minor1LtMovementCapacity =
      minor1LtHelpers.potentialCapacity *
      minor1LtPPrime *
      (1 - errDiv(this.volumes.Minor2.RT, this.minor2RtMovementCapacity));
    this.minor1LtVc = errDiv(
      this.volumes.Minor1.LT,
      this.minor1LtMovementCapacity
    );
    this.minor2LtMovementCapacity =
      minor2LtHelpers.potentialCapacity *
      minor2LtPPrime *
      (1 - errDiv(this.volumes.Minor1.RT, this.minor1RtMovementCapacity));
    this.minor2LtVc = errDiv(
      this.volumes.Minor2.LT,
      this.minor2LtMovementCapacity
    );

    this._resultsByZone.Z5.VC = Math.max(
      this.major1LtVc,
      this.major1ThruVc,
      this.major2LtVc,
      this.major2ThruVc,
      this.minor1LtVc,
      this.minor1ThruVc,
      this.minor1RtVc,
      this.minor2LtVc,
      this.minor2ThruVc,
      this.minor2RtVc
    );

    // Two Stage Movement Calculations
    if (this.LaneConfig.Z5.median !== TWSC_MEDIAN_OPTIONS.NO) {
      const numMovement =
        this.LaneConfig.Z5.median === TWSC_MEDIAN_OPTIONS.YES_NARROW ? 1 : 2;
      const alpha = 1 - 0.32 * Math.exp(-1.3 * Math.sqrt(numMovement));

      // Two stage minor approach thru v/c
      this.minor1ThruCapacityByStage = getThruCapacityByStage({
        stage1LtVolume: this.volumes.Major1.LT,
        stage1ThruVolume: this.volumes.Major1.T,
        stage1RtVolume: this.volumes.Major1.RT,
        stage1RtLanes: this.LaneConfig.Z5.Major1.RT,
        stage1Factor: major1SharedLaneVcRatio,
        stage2LtVolume: this.volumes.Major2.LT,
        stage2ThruVolume: this.volumes.Major2.T,
        stage2RtVolume: this.volumes.Major2.RT,
        stage2Factor: major2SharedLaneVcRatio,
        criticalHeadway: minor1ThruHelpers.criticalHeadway,
        followupHeadway: minor1ThruHelpers.followupHeadway,
      });
      const minor1ThruY = errDiv(
        this.minor1ThruCapacityByStage.stage1Capacity -
          this.minor1ThruMovementCapacity,
        this.minor1ThruCapacityByStage.stage2Capacity -
          this.volumes.Major1.LT -
          this.minor1ThruMovementCapacity
      );

      this.minor1ThruTwoStageCapacity =
        minor1ThruY === 1
          ? errDiv(alpha, numMovement + 1) *
            (numMovement *
              (this.minor1ThruCapacityByStage.stage2Capacity -
                this.volumes.Major1.LT) +
              minor1ThruHelpers.potentialCapacity * major1SharedLaneVcRatio)
          : errDiv(alpha, Math.pow(minor1ThruY, numMovement + 1) - 1) *
            (minor1ThruY *
              (Math.pow(minor1ThruY, numMovement) - 1) *
              (this.minor1ThruCapacityByStage.stage2Capacity -
                this.volumes.Major1.LT) +
              (minor1ThruY - 1) *
                (minor1ThruHelpers.potentialCapacity *
                  (1 -
                    errDiv(
                      this.volumes.Major1.LT,
                      this.major1SharedLaneCapacity
                    ))));
      this.minor1ThruTwoStageVc = errDiv(
        this.volumes.Minor1.T,
        this.minor1ThruTwoStageCapacity
      );

      this.minor2ThruCapacityByStage = getThruCapacityByStage({
        stage1LtVolume: this.volumes.Major2.LT,
        stage1ThruVolume: this.volumes.Major2.T,
        stage1RtVolume: this.volumes.Major2.RT,
        stage1RtLanes: this.LaneConfig.Z5.Major2.RT,
        stage1Factor: major2SharedLaneVcRatio,
        stage2LtVolume: this.volumes.Major1.LT,
        stage2ThruVolume: this.volumes.Major1.T,
        stage2RtVolume: this.volumes.Major1.RT,
        stage2Factor: major1SharedLaneVcRatio,
        criticalHeadway: minor2ThruHelpers.criticalHeadway,
        followupHeadway: minor2ThruHelpers.followupHeadway,
      });
      const minor2ThruY = errDiv(
        this.minor2ThruCapacityByStage.stage1Capacity -
          this.minor2ThruMovementCapacity,
        this.minor2ThruCapacityByStage.stage2Capacity -
          this.volumes.Major2.LT -
          this.minor2ThruMovementCapacity
      );
      this.minor2ThruTwoStageCapacity =
        minor2ThruY === 1
          ? errDiv(alpha, numMovement + 1) *
            (numMovement *
              (this.minor2ThruCapacityByStage.stage2Capacity -
                this.volumes.Major2.LT) +
              minor2ThruHelpers.potentialCapacity * major2SharedLaneVcRatio)
          : errDiv(alpha, Math.pow(minor2ThruY, numMovement + 1) - 1) *
            (minor2ThruY *
              (Math.pow(minor2ThruY, numMovement) - 1) *
              (this.minor2ThruCapacityByStage.stage2Capacity -
                this.volumes.Major2.LT) +
              (minor2ThruY - 1) *
                (minor2ThruHelpers.potentialCapacity *
                  major2SharedLaneVcRatio));
      this.minor2ThruTwoStageVc = errDiv(
        this.volumes.Minor2.T,
        this.minor2ThruTwoStageCapacity
      );

      // Two stage minor approach left turn V/C
      this.minor1LtCapacityByStage = getLtCapacityByStage({
        stage1LtVolume: this.volumes.Major1.LT,
        stage1ThruVolume: this.volumes.Major1.T,
        stage1RtVolume: major1RtVolumeExclusiveLane,
        opposingRtFactor:
          1 - errDiv(this.volumes.Minor2.RT, this.minor2RtMovementCapacity),
        stage2LtVolume: this.volumes.Major2.LT,
        stage2ThruVolume: this.volumes.Major2.T,
        stage2RtVolume: this.volumes.Major2.RT,
        biDirectionalSixLaneFactor:
          6.7 +
          (this.LaneConfig.Z5.Major1.T === 1 ? 1 : 2) * this.truckPcts.Minor1 -
          (sumValues(this.volumes.Minor2) === 0 ? 0.7 : 0),
        conflictingLanes: this.LaneConfig.Z5.Major1.T,
        pPrime: minor1LtPPrime,
        opposingThruVolume: this.volumes.Minor2.T,
        opposingRtVolume: this.volumes.Minor2.RT,
        criticalHeadway: minor1LtHelpers.criticalHeadway,
        followupHeadway: minor1LtHelpers.followupHeadway,
      });
      const minor1LtY = errDiv(
        this.minor1LtCapacityByStage.stage1Capacity -
          this.minor1LtMovementCapacity,
        this.minor1LtCapacityByStage.stage2Capacity -
          this.volumes.Major1.LT -
          this.minor1LtMovementCapacity
      );
      this.minor1LtTwoStageCapacity =
        minor1LtY === 1
          ? (alpha / (numMovement + 1)) *
            (numMovement *
              (this.minor1LtCapacityByStage.stage2Capacity -
                this.volumes.Major1.LT) +
              minor1LtHelpers.potentialCapacity *
                (major1SharedLaneVcRatio < 0 ? 0.01 : major1SharedLaneVcRatio))
          : errDiv(alpha, Math.pow(minor1LtY, numMovement + 1) - 1) *
            (minor1LtY *
              (Math.pow(minor1LtY, numMovement) - 1) *
              (this.minor1LtCapacityByStage.stage2Capacity -
                this.volumes.Major1.LT) +
              (minor1LtY - 1) *
                (minor1LtHelpers.potentialCapacity *
                  (major1SharedLaneVcRatio < 0
                    ? 0.01
                    : major1SharedLaneVcRatio)));
      this.minor1LtTwoStageVc = errDiv(
        this.volumes.Minor1.LT,
        this.minor1LtTwoStageCapacity
      );

      this.minor2LtCapacityByStage = getLtCapacityByStage({
        stage1LtVolume: this.volumes.Major2.LT,
        stage1ThruVolume: this.volumes.Major2.T,
        stage1RtVolume: major2RtVolumeExclusiveLane,
        opposingRtFactor:
          1 - errDiv(this.volumes.Minor1.RT, this.minor1RtMovementCapacity),
        stage2LtVolume: this.volumes.Major1.LT,
        stage2ThruVolume: this.volumes.Major1.T,
        stage2RtVolume: this.volumes.Major1.RT,
        biDirectionalSixLaneFactor:
          6.7 +
          (this.LaneConfig.Z5.Major2.T === 1 ? 1 : 2) * this.truckPcts.Minor2 -
          (sumValues(this.volumes.Minor1) === 0 ? 0.7 : 0),
        conflictingLanes: this.LaneConfig.Z5.Major2.T,
        pPrime: minor2LtPPrime,
        opposingThruVolume: this.volumes.Minor1.T,
        opposingRtVolume: this.volumes.Minor1.RT,
        criticalHeadway: minor2LtHelpers.criticalHeadway,
        followupHeadway: minor2LtHelpers.followupHeadway,
      });
      const minor2LtY = errDiv(
        this.minor2LtCapacityByStage.stage1Capacity -
          this.minor2LtMovementCapacity,
        this.minor2LtCapacityByStage.stage2Capacity -
          this.volumes.Major2.LT -
          this.minor2LtMovementCapacity
      );
      this.minor2LtTwoStageCapacity =
        minor2LtY === 1
          ? (alpha / (numMovement + 1)) *
            (numMovement *
              (this.minor2LtCapacityByStage.stage2Capacity -
                this.volumes.Major2.LT) +
              minor2LtHelpers.potentialCapacity *
                (major2SharedLaneVcRatio < 0 ? 0.01 : major2SharedLaneVcRatio))
          : errDiv(alpha, Math.pow(minor2LtY, numMovement + 1) - 1) *
            (minor2LtY *
              (Math.pow(minor2LtY, numMovement) - 1) *
              (this.minor2LtCapacityByStage.stage2Capacity -
                this.volumes.Major2.LT) +
              (minor2LtY - 1) *
                (minor2LtHelpers.potentialCapacity *
                  (major2SharedLaneVcRatio < 0
                    ? 0.01
                    : major2SharedLaneVcRatio)));
      this.minor2LtTwoStageVc = errDiv(
        this.volumes.Minor2.LT,
        this.minor2LtTwoStageCapacity
      );
      this._resultsByZone.Z5.VC = Math.max(
        this.major1LtVc,
        this.major1ThruVc,
        this.major2LtVc,
        this.major2ThruVc,
        this.minor1LtTwoStageVc,
        this.minor1ThruTwoStageVc,
        this.minor1RtVc,
        this.minor2LtTwoStageVc,
        this.minor2ThruTwoStageVc,
        this.minor2RtVc
      );
    }
  }

  getAdjustedVolume() {
    return this.majorStDirection === ORIENTATIONS.EAST_WEST
      ? {
          Major1: {
            LT: this.EBL_MASTER,
            T:
              this.EBT_MASTER +
              (this.LaneConfig.Z5.Major1.RT === 0 ? this.EBR_MASTER : 0),
            RT: this.LaneConfig.Z5.Major1.RT === 0 ? 0 : this.EBR_MASTER,
          },
          Major2: {
            LT: this.WBL_MASTER,
            T:
              this.WBT_MASTER +
              (this.LaneConfig.Z5.Major2.RT === 0 ? this.WBR_MASTER : 0),
            RT: this.LaneConfig.Z5.Major2.RT === 0 ? 0 : this.WBR_MASTER,
          },
          Minor1: {
            LT: this.LaneConfig.Z5.Minor1.LT === 0 ? 0 : this.NBL_MASTER,
            T:
              this.NBT_MASTER +
              (this.LaneConfig.Z5.Minor1.RT === 0 ? this.NBR_MASTER : 0) +
              (this.LaneConfig.Z5.Minor1.LT === 0 ? this.NBL_MASTER : 0),
            RT: this.LaneConfig.Z5.Minor1.RT === 0 ? 0 : this.NBR_MASTER,
          },
          Minor2: {
            LT: this.LaneConfig.Z5.Minor2.LT === 0 ? 0 : this.SBL_MASTER,
            T:
              this.SBT_MASTER +
              (this.LaneConfig.Z5.Minor2.RT === 0 ? this.SBR_MASTER : 0) +
              (this.LaneConfig.Z5.Minor2.LT === 0 ? this.SBL_MASTER : 0),
            RT: this.LaneConfig.Z5.Minor2.RT === 0 ? 0 : this.SBR_MASTER,
          },
        }
      : {
          Major1: {
            LT: this.SBL_MASTER,
            T:
              this.SBT_MASTER +
              (this.LaneConfig.Z5.Major1.RT === 0 ? this.SBR_MASTER : 0),
            RT: this.LaneConfig.Z5.Major1.RT === 0 ? 0 : this.SBR_MASTER,
          },
          Major2: {
            LT: this.NBL_MASTER,
            T:
              this.NBT_MASTER +
              (this.LaneConfig.Z5.Major2.RT === 0 ? this.NBR_MASTER : 0),
            RT: this.LaneConfig.Z5.Major2.RT === 0 ? 0 : this.NBR_MASTER,
          },
          Minor1: {
            LT: this.LaneConfig.Z5.Minor1.LT === 0 ? 0 : this.EBL_MASTER,
            T:
              this.EBT_MASTER +
              (this.LaneConfig.Z5.Minor1.RT === 0 ? this.EBR_MASTER : 0) +
              (this.LaneConfig.Z5.Minor1.LT === 0 ? this.EBL_MASTER : 0),
            RT: this.LaneConfig.Z5.Minor1.RT === 0 ? 0 : this.EBR_MASTER,
          },
          Minor2: {
            LT: this.LaneConfig.Z5.Minor2.LT === 0 ? 0 : this.WBL_MASTER,
            T:
              this.WBT_MASTER +
              (this.LaneConfig.Z5.Minor2.RT === 0 ? this.WBR_MASTER : 0) +
              (this.LaneConfig.Z5.Minor2.LT === 0 ? this.WBL_MASTER : 0),
            RT: this.LaneConfig.Z5.Minor2.RT === 0 ? 0 : this.WBR_MASTER,
          },
        };
  }
}

export const getMajorStLtMovementCapacity = ({
  conflictingLanes,
  conflictingVolume,
  truckPct,
}) => {
  // Helper constants
  const tc_base = conflictingLanes === 3 ? 5.3 : 4.1; // Critical headway, base
  const tf_base = conflictingLanes === 3 ? 3.1 : 2.2; // Followup headway, base
  const tc_hv = conflictingLanes === 1 ? 1 : 2; // Critical headway, heavy vehicle
  const tf_hv = conflictingLanes === 1 ? 0.9 : 1; // Followup headway, heavy vehicle

  // No left turn adjustment for this movement
  // Movement capacity = potential capacity for this movement
  return getPotentialCapacity({
    tc_base,
    tf_base,
    tc_hv,
    tf_hv,
    t3_lt: 0,
    truckPct,
    conflictingVolume,
  });
};

export const getMinorStRtMovementCapacity = ({
  conflictingLanes,
  conflictingVolume,
  truckPct,
}) => {
  // Helper constants
  const tc_base =
    conflictingLanes === 1 ? 6.2 : conflictingLanes === 2 ? 6.9 : 7.1; // Critical headway, base
  const tf_base = conflictingLanes === 3 ? 3.9 : 3.3; // Followup headway, base
  const tc_hv = conflictingLanes === 1 ? 1 : 2; // Critical headway, heavy vehicle
  const tf_hv = conflictingLanes === 1 ? 0.9 : 1; // Followup headway, heavy vehicle

  // No left turn adjustment for this movement
  // Movement capacity = potential capacity for this movement
  return getPotentialCapacity({
    tc_base,
    tf_base,
    tc_hv,
    tf_hv,
    t3_lt: 0,
    truckPct,
    conflictingVolume,
  });
};

/**
 *
 * @param {number} conflictingLanes Number of conflicting thru lanes on major approach
 * @param {number} opposingLanes Total volume from opposing direction
 * @returns {object} Object of critical headway, followup headway, and potential capacity
 */
export const getMinorStThruHelpers = ({
  conflictingLanes,
  conflictingVolume,
  opposingVolume,
  truckPct,
}) => {
  // Helper constants
  const tc_hv = conflictingLanes === 1 ? 1 : 2; // Critical headway, heavy vehicle
  const tf_hv = conflictingLanes === 1 ? 0.9 : 1; // Followup headway, heavy vehicle
  const t3_lt = opposingVolume === 0 ? 0.7 : 0; // Left turn adjustment

  const potentialCapacity = getPotentialCapacity({
    tc_base: 6.5,
    tf_base: 4,
    tc_hv,
    tf_hv,
    t3_lt,
    truckPct,
    conflictingVolume,
  });
  return {
    criticalHeadway: 6.5 + tc_hv * truckPct - t3_lt,
    followupHeadway: 4 + tf_hv * truckPct,
    potentialCapacity,
  };
};

/**
 *
 * @param {number} tc_base Base critical headway
 * @param {number} tf_base Base followup headway
 * @param {number} tc_hv Heavy vechile critical headway
 * @param {number} tf_hv Heavy vechile followup headway
 * @param {number} t3_lt Left turn adjustment
 * @param {number} truckPct Percentage of heavy vehciles
 * @param {number} conflictingVolume
 * @returns {number} Potential capacity of the movement
 */
const getPotentialCapacity = ({
  tc_base,
  tf_base,
  tc_hv,
  tf_hv,
  t3_lt,
  truckPct,
  conflictingVolume,
}) => {
  const criticalHeadway = tc_base + tc_hv * truckPct - t3_lt;
  const followupHeadway = tf_base + tf_hv * truckPct;
  return (
    conflictingVolume *
    (Math.exp(-(conflictingVolume * criticalHeadway) / 3600) /
      (1 - Math.exp(-(conflictingVolume * followupHeadway) / 3600)))
  );
};

export const getThruCapacityByStage = ({
  stage1LtVolume,
  stage1ThruVolume,
  stage1RtVolume,
  stage1RtLanes,
  stage1Factor,
  stage2LtVolume,
  stage2ThruVolume,
  stage2RtVolume,
  stage2Factor,
  criticalHeadway,
  followupHeadway,
}) => {
  const stage1ConflictingFlow =
    2 * stage1LtVolume +
    stage1ThruVolume +
    (stage1RtLanes > 0 ? 0 : 0.5 * stage1RtVolume);
  const stage2ConflictingFlow =
    2 * stage2LtVolume + stage2ThruVolume + stage2RtVolume;
  const stage1Capacity =
    stage1ConflictingFlow *
    errDiv(
      Math.exp((-stage1ConflictingFlow * criticalHeadway) / 3600),
      1 - Math.exp((-stage1ConflictingFlow * followupHeadway) / 3600)
    ) *
    stage1Factor;
  const stage2Capacity =
    stage2ConflictingFlow *
    errDiv(
      Math.exp((-stage2ConflictingFlow * criticalHeadway) / 3600),
      1 - Math.exp((-stage2ConflictingFlow * followupHeadway) / 3600)
    ) *
    stage2Factor;
  return { stage1Capacity, stage2Capacity };
};

export const getMinorStLtPPrime = ({
  stage1LtVolume,
  stage1SharedLaneCapacity,
  stage2LtVolume,
  stage2SharedLaneCapacity,
  opposingThruVolume,
  opposingMovementCapacity,
}) => {
  const stage1Factor = 1 - errDiv(stage1LtVolume, stage1SharedLaneCapacity);
  if (stage1Factor < 0) {
    return 0.01;
  }
  const stage2Factor =
    1 - errDiv(stage2LtVolume, stage2SharedLaneCapacity) < 0
      ? 0.01
      : 1 - errDiv(stage2LtVolume, stage2SharedLaneCapacity);
  const opposingFactor =
    1 - errDiv(opposingThruVolume, opposingMovementCapacity) < 0
      ? 0.01
      : 1 - errDiv(opposingThruVolume, opposingMovementCapacity);
  const pDoublePrime = stage1Factor * stage2Factor * opposingFactor;
  return (
    0.65 * pDoublePrime -
    errDiv(pDoublePrime, pDoublePrime + 3) +
    0.6 * Math.sqrt(pDoublePrime)
  );
};

/**
 *
 * @returns {object} Object of critical headway, followup headway, and potential capacity
 */
export const getMinorStLtHelpers = ({
  conflictingLanes,
  conflictingVolume,
  opposingVolume,
  truckPct,
}) => {
  // Helper constants
  const tc_base =
    conflictingLanes === 1 ? 7.1 : conflictingLanes === 2 ? 7.5 : 6.4; // Critical headway, base
  const tf_base = conflictingLanes === 3 ? 3.8 : 3.5; // Followup headway, base
  const tc_hv = conflictingLanes === 1 ? 1 : 2; // Critical headway, heavy vehicle
  const tf_hv = conflictingLanes === 1 ? 0.9 : 1; // Followup headway, heavy vehicle
  const t3_lt = opposingVolume === 0 ? 0.7 : 0; // Left turn adjustment

  const potentialCapacity = getPotentialCapacity({
    tc_base,
    tf_base,
    tc_hv,
    tf_hv,
    t3_lt,
    truckPct,
    conflictingVolume,
  });
  return {
    criticalHeadway: tc_base + tc_hv * truckPct - t3_lt,
    followupHeadway: tf_base + tf_hv * truckPct,
    potentialCapacity,
  };
};

export const getLtCapacityByStage = ({
  stage1LtVolume,
  stage1ThruVolume,
  stage1RtVolume,
  opposingRtFactor,
  stage2LtVolume,
  stage2ThruVolume,
  stage2RtVolume,
  biDirectionalSixLaneFactor,
  conflictingLanes,
  pPrime,
  opposingThruVolume,
  opposingRtVolume,
  criticalHeadway,
  followupHeadway,
}) => {
  const stage1ConflictingFlow =
    2 * stage1LtVolume + stage1ThruVolume + 0.5 * stage1RtVolume;
  const stage2ConflictingFlow =
    2 * stage2LtVolume +
    (conflictingLanes === 1 ? 1 : conflictingLanes === 2 ? 0.5 : 0.4) *
      stage2ThruVolume +
    (conflictingLanes === 1
      ? 0.5 * stage2RtVolume + 0.5 * opposingRtVolume
      : 0) +
    0.5 * opposingThruVolume;
  const stage1Capacity =
    stage1ConflictingFlow *
    errDiv(
      Math.exp((-stage1ConflictingFlow * criticalHeadway) / 3600),
      1 - Math.exp((-stage1ConflictingFlow * followupHeadway) / 3600)
    ) *
    pPrime *
    opposingRtFactor;
  const stage2Capacity =
    stage2ConflictingFlow *
    errDiv(
      Math.exp(
        (-stage2ConflictingFlow *
          (conflictingLanes === 3
            ? biDirectionalSixLaneFactor
            : criticalHeadway)) /
          3600
      ),
      1 - Math.exp((-stage2ConflictingFlow * followupHeadway) / 3600)
    ) *
    pPrime *
    opposingRtFactor;
  return { stage1Capacity, stage2Capacity };
};
